"use client";

import * as motion from "framer-motion/client";
import { cva, type VariantProps } from "class-variance-authority";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon } from "lucide-react";

const breakCtaVariants = cva(
  "w-full py-24 px-6 md:px-8 mb-12",
  {
    variants: {
      variant: {
        primary: "bg-linear-to-br from-orange-600 to-orange-400",
        secondary: "bg-linear-to-br from-primary-700 to-primary-500"
      }
    },
    defaultVariants: {
      variant: "primary"
    }
  }
);

const titleVariants = cva(
  "text-3xl md:text-4xl font-bold mb-4 break-words",
  {
    variants: {
      variant: {
        primary: "text-white",
        secondary: "text-orange-400"
      }
    },
    defaultVariants: {
      variant: "primary"
    }
  }
);

const descriptionVariants = cva(
  "text-lg md:text-xl mb-8 max-w-2xl mx-auto",
  {
    variants: {
      variant: {
        primary: "text-white/90",
        secondary: "text-white"
      }
    },
    defaultVariants: {
      variant: "primary"
    }
  }
);

interface BreakCTAProps extends VariantProps<typeof breakCtaVariants> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  buttonText?: string;
}

export const BreakCTA = ({
  variant = "primary",
  title,
  description,
  buttonText = "Jetzt loslegen",
}: BreakCTAProps) => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={breakCtaVariants({ variant })}
    >
      <div className="container mx-auto max-w-4xl text-center">
        <h2 className={titleVariants({ variant })}>
          {title}
        </h2>
        <p className={descriptionVariants({ variant })}>
          {description}
        </p>
        <Button
          size="lg"
          onClick={scrollToTop}
          variant={variant === "primary" ? "outlinedark" : "cta"}
          className={variant === "primary" ? "font-medium text-white" : "font-medium"}
          right={<ArrowRightIcon className="w-4 h-4" />}
        >
          {buttonText}
        </Button>
      </div>
    </motion.div>
  );
};